import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { IconBadge } from '../components/icons/ServiceIcons';
import './ServicePage.css';
const appDevelopmentArtwork = '/assets/illustrations/app-development-experience.svg';

const AppDevelopment = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const capabilities = [
    {
      icon: 'pen',
      title: 'Product Strategy & UX',
      description: 'Discovery, journey mapping, and rapid prototyping to validate desirability and align to business KPIs.'
    },
    {
      icon: 'layers',
      title: 'Full-Stack Engineering',
      description: 'Modern web, mobile, and API development with automated testing and continuous delivery baked in.'
    },
    {
      icon: 'shield',
      title: 'Secure-by-Design Architecture',
      description: 'Identity, compliance, and data protection patterns implemented across every layer of the solution.'
    },
    {
      icon: 'beaker',
      title: 'Quality Engineering',
      description: 'Automated functional, performance, and accessibility testing integrated into CI/CD workflows.'
    },
    {
      icon: 'analytics',
      title: 'Analytics & Experimentation',
      description: 'Instrumentation, feature flags, and experimentation frameworks to iterate with confidence.'
    },
    {
      icon: 'toolkit',
      title: 'Managed Services',
      description: 'Runbooks, DevSecOps, and support teams to sustain and evolve your product after launch.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discover',
      description: 'Stakeholder workshops, user research, and backlog definition grounded in measurable outcomes.',
      icon: 'assessment'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Experience design, architecture planning, and sprint planning with a validated release roadmap.',
      icon: 'architecture'
    },
    {
      step: '03',
      title: 'Build',
      description: 'Iterative engineering with design and QA in lockstep, supported by continuous integration pipelines.',
      icon: 'toolkit'
    },
    {
      step: '04',
      title: 'Launch',
      description: 'Cutover planning, release orchestration, and readiness training to deliver a flawless go-live.',
      icon: 'rocket'
    },
    {
      step: '05',
      title: 'Operate',
      description: 'Support, optimisation, and product growth services that keep experiences relevant and high-performing.',
      icon: 'analytics'
    }
  ];

  const deliveryModels = [
    {
      title: 'Dedicated Product Squads',
      description: 'Cross-functional teams led by CloudRaize delivering roadmap ownership, ceremonies, and communications.',
      icon: 'people'
    },
    {
      title: 'Co-Delivery & Enablement',
      description: 'Pair engineers and designers with your staff to upskill in modern practices while shipping critical milestones.',
      icon: 'automation'
    },
    {
      title: 'Design System Factory',
      description: 'Establish reusable UI libraries, accessibility standards, and design ops processes for consistency.',
      icon: 'layers'
    },
    {
      title: 'Mobile Centres of Excellence',
      description: 'Native and cross-platform expertise with release engineering, store optimisation, and observability.',
      icon: 'mobile'
    }
  ];

  const technologies = [
    {
      name: 'React & React Native',
      tagline: 'Modern UI',
      description: 'Component-driven web and mobile experiences with shared design systems.',
      icon: 'layers'
    },
    {
      name: 'Angular & Vue.js',
      tagline: 'SPA Frameworks',
      description: 'Enterprise-ready single page applications with opinionated tooling and testing.',
      icon: 'code'
    },
    {
      name: 'Next.js & Remix',
      tagline: 'Full-stack React',
      description: 'SSR, edge rendering, and API routes for performance-centric user experiences.',
      icon: 'layers'
    },
    {
      name: 'Node.js & NestJS',
      tagline: 'Backend Services',
      description: 'Scalable APIs, event-driven services, and streaming architectures.',
      icon: 'code'
    },
    {
      name: 'Spring Boot & Quarkus',
      tagline: 'Java Platforms',
      description: 'Cloud-native JVM services with observability, security, and testing baked in.',
      icon: 'code'
    },
    {
      name: 'GraphQL & gRPC',
      tagline: 'API Mesh',
      description: 'Typed contracts, federation, and streaming APIs connecting microservices.',
      icon: 'api'
    },
    {
      name: 'Kotlin, Swift & Flutter',
      tagline: 'Mobile Excellence',
      description: 'Native and cross-platform delivery with automated release engineering.',
      icon: 'mobile'
    },
    {
      name: 'Design Systems (Figma, Storybook)',
      tagline: 'Experience Ops',
      description: 'Token-driven design, accessibility standards, and reusable component libraries.',
      icon: 'pen'
    },
    {
      name: 'Cypress, Playwright & Jest',
      tagline: 'Quality Engineering',
      description: 'Automated functional, visual, and performance testing inside CI pipelines.',
      icon: 'beaker'
    },
    {
      name: 'Azure DevOps & GitHub Actions',
      tagline: 'Delivery Platforms',
      description: 'Boards, pipelines, security scans, and deployment automation across environments.',
      icon: 'cloud'
    },
    {
      name: 'Terraform & Pulumi',
      tagline: 'Infrastructure as Code',
      description: 'Immutable infrastructure definitions for app hosting, networking, and security.',
      icon: 'infrastructure'
    },
    {
      name: 'Firebase & Supabase',
      tagline: 'Cloud Services',
      description: 'Realtime data, authentication, and serverless functions to accelerate MVPs.',
      icon: 'spark'
    }
  ];

  const marqueeRows = [
    technologies.filter((_, index) => index % 2 === 0),
    technologies.filter((_, index) => index % 2 === 1)
  ].filter((row) => row.length);

  return (
    <motion.div 
      className="service-page app-development-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <section className="service-hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <motion.div 
            className="hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-icon"
              initial={{ scale: 0 }}
              animate={heroInView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={appDevelopmentArtwork} 
                alt="Abstract app development illustration" 
              />
            </motion.div>
            <h1>Application Development</h1>
            <p className="hero-description">
              Launch experience-led products faster with squads that blend strategy, design, engineering, and DevOps. We own the journey from validated concept to secure, support-ready releases.
            </p>
            <Link to="/contact" className="btn">Build Your App</Link>
          </motion.div>
        </div>
      </section>

      <section className="what-we-do section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What We Do</h2>
            <p>
              CloudRaize forms integrated product teams that move ideas from vision to market-ready experiences. We balance business strategy, engineering velocity, and user delight while maintaining enterprise-grade governance.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="features-section section section-alt" ref={featuresRef}>
        <div className="container">
          <div className="grid-3">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                className="feature-card card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconBadge name={capability.icon} size={72} backgroundOpacity={0.24} />
                </div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section section" ref={processRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Product Delivery Framework</h2>
            <p>Transparent, outcome-driven phases that keep stakeholders aligned</p>
          </motion.div>

          <div className="process-timeline">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className="process-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="process-number">{item.step}</div>
                <div className="process-content">
                  <div className="process-icon">
                    <IconBadge
                      name={item.icon}
                      className="icon-badge--sm"
                      size={58}
                      backgroundOpacity={0.24}
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="use-cases-section section section-alt">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Engagement Models</h2>
            <p>Flexible delivery structures to fit your organisation and roadmap</p>
          </motion.div>

          <div className="grid-4">
            {deliveryModels.map((model, index) => (
              <motion.div
                key={model.title}
                className="use-case-item card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <div className="use-case-icon">
                  <IconBadge
                    name={model.icon}
                    className="icon-badge--sm"
                    size={56}
                    backgroundOpacity={0.24}
                  />
                </div>
                <h3>{model.title}</h3>
                <p>{model.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="technologies-section section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Technology Stack</h2>
            <p>Modern frameworks, pipelines, and platforms we deliver with every engagement</p>
          </motion.div>

          <motion.div
            className="tech-marquee-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {marqueeRows.map((row, rowIndex) => (
              <div
                className={`tech-marquee${rowIndex % 2 === 1 ? ' tech-marquee--reverse' : ''}`}
                key={`appdev-row-${rowIndex}`}
              >
                <div className="tech-marquee__track">
                  {[...row, ...row].map((tech, index) => (
                    <div className="tech-marquee__card" key={`appdev-tech-${rowIndex}-${index}-${tech.name}`}>
                      <IconBadge
                        name={tech.icon}
                        className="tech-marquee__badge icon-badge--sm"
                        size={58}
                        backgroundOpacity={0.24}
                      />
                      <div className="tech-marquee__label">
                        <span className="tech-marquee__title">{tech.name}</span>
                        {tech.tagline && (
                          <span className="tech-marquee__subtitle">{tech.tagline}</span>
                        )}
                        {tech.description && (
                          <span className="tech-marquee__description">{tech.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default AppDevelopment;
